import React, { useCallback, memo } from 'react'

import {
  Button,
  classnames,
  ThreeDOnly,
  ThreeDPrintOnly,
  Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { useSearchPageStore } from 'Pods/SearchPage/Store'
import { setAssetType } from 'Pods/SearchPage/Store/actions/assetType'
import { ASSET_TYPE } from 'Pods/SearchPage/Store/constants'

const threeDIcon = <Icon svgComponent={ThreeDOnly} size="small" notSpaced />
const threeDPrintIcon = <Icon svgComponent={ThreeDPrintOnly} size="small" notSpaced />

function AssetTypeToggle() {
  const translate = useTranslate()

  const assetType = useSearchPageStore(state => state.assetType)

  const handleAllClick = useCallback(() => {
    setAssetType(ASSET_TYPE.ALL)
  }, [])

  const handleThreeDOnlyClick = useCallback(() => {
    setAssetType(ASSET_TYPE.THREE_D_ONLY)
  }, [])

  const handleThreeDPrintOnlyClick = useCallback(() => {
    setAssetType(ASSET_TYPE.THREE_D_PRINT_ONLY)
  }, [])

  const isAllActive = ASSET_TYPE.ALL === assetType
  const isThreeDOnlyActive = ASSET_TYPE.THREE_D_ONLY === assetType
  const isThreeDPrintOnlyActive = ASSET_TYPE.THREE_D_PRINT_ONLY === assetType

  // `isActive` only drives styling, so the selected asset type is invisible to assistive tech
  // without aria-pressed. The group is a mutually exclusive toggle set, not navigation, so
  // aria-pressed is the correct state (aria-current would imply a location).
  return (
    <div
      className="asset-type-toggle"
      role="group"
      aria-label={translate('search_page.filters.asset_type.group_label')}
    >
      <Button
        onClick={handleAllClick}
        isActive={isAllActive}
        type={isAllActive ? 'primary' : 'secondary'}
        customClass={classnames(
          'asset-type-toggle__button',
          isAllActive && 'asset-type-toggle__button--is-active',
        )}
        actionType="button"
        aria-pressed={isAllActive}
        aria-label={translate('search_page.filters.asset_type.all')}
      >
        <span className="asset-type-toggle__all-label">
          {translate('search_page.filters.asset_type.all')}
        </span>
      </Button>
      <Button
        onClick={handleThreeDOnlyClick}
        variant="with-icon"
        icon={threeDIcon}
        isActive={isThreeDOnlyActive}
        type={isThreeDOnlyActive ? 'primary' : 'secondary'}
        customClass={classnames(
          'asset-type-toggle__button',
          isThreeDOnlyActive && 'asset-type-toggle__button--is-active',
        )}
        actionType="button"
        aria-pressed={isThreeDOnlyActive}
        aria-label={translate('search_page.filters.asset_type.three_d_only')}
      >
        <span className="asset-type-toggle__label desktop-only">
          {translate('search_page.filters.asset_type.three_d_only')}
        </span>
        <span className="asset-type-toggle__label mobile-only">
          {translate('search_page.filters.asset_type.three_d_only_short')}
        </span>
      </Button>
      <Button
        onClick={handleThreeDPrintOnlyClick}
        variant="with-icon"
        icon={threeDPrintIcon}
        isActive={isThreeDPrintOnlyActive}
        type={isThreeDPrintOnlyActive ? 'primary' : 'secondary'}
        customClass={classnames(
          'asset-type-toggle__button',
          isThreeDPrintOnlyActive && 'asset-type-toggle__button--is-active',
        )}
        actionType="button"
        aria-pressed={isThreeDPrintOnlyActive}
        aria-label={translate('search_page.filters.asset_type.three_d_print_only')}
      >
        <span className="asset-type-toggle__label desktop-only">
          {translate('search_page.filters.asset_type.three_d_print_only')}
        </span>
        <span className="asset-type-toggle__label mobile-only">
          {translate('search_page.filters.asset_type.three_d_print_only_short')}
        </span>
      </Button>
    </div>
  )
}

export default memo(AssetTypeToggle)
